/**
 * Per-endpoint maps from a prism-new `data` payload (see the prism-new swagger) back to the payload
 * the old API returned for the same call, so existing screens keep reading the fields they read today.
 * Keys use the same `METHOD /path` form as prism-new-endpoints.ts; endpoints with no entry pass through.
 */

import { BOT_BUILDER_LEGACY_MAPS } from './prism-new-legacy-maps.bot-builder';
import { CALL_AUDIT_LEGACY_MAPS } from './prism-new-legacy-maps.call-audit';
import { LEAD_AGENTS_LEGACY_MAPS } from './prism-new-legacy-maps.lead-agents';
import { RAG_REQUEST_FIXES } from './prism-new-legacy-maps.rag';
import { LegacyContext, LegacyMap, LegacyPayload, has, pageInfo, rows, unwrap } from './prism-new-legacy-maps.helpers';

/** Old monthly records carried `YYYY-MM`; prism-new sends the first day, `YYYY-MM-01`. */
const month = (date: unknown) => (typeof date === 'string' && /^\d{4}-\d{2}-01$/.test(date) ? date.slice(0, 7) : date);

const withPlatform = (r: any) => ({
  ...r, ...('date' in r ? { date: month(r.date) } : {}), platform: r.platform ?? { pid: r.platformPid, name: r.platformName },
});
const withCountry = (c: any) => ({ ...c, country: c.country ?? { id: c.countryId, name: c.countryName, code: c.countryCode } });

const SOCIAL_METRICS = ['views', 'impressions', 'follower', 'reach', 'watchTime', 'engagement', 'contentInteraction', 'subscribers', 'reachedAudience', 'uniqueViewers', 'follow'];

function nextMonth(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

/** Old `/campaign-metrics` summed spend per period (every month of the range for `unit=month`). */
function campaignPeriods(d: any, ctx: LegacyContext): LegacyPayload {
  if (!Array.isArray(d?.metrics)) return { data: d };
  const unit = String(ctx.params['unit'] || 'month');
  const length = unit === 'year' ? 4 : unit === 'month' ? 7 : 10;
  const periods = new Map<string, { period: string; cost: number; budget: number }>();
  const start = String(ctx.params['startDate'] ?? ''), end = String(ctx.params['endDate'] ?? '');
  if (unit === 'month' && /^\d{4}-\d{2}$/.test(start) && /^\d{4}-\d{2}$/.test(end)) {
    for (let p = start; p <= end; p = nextMonth(p)) periods.set(p, { period: p, cost: 0, budget: 0 });
  }
  for (const m of d.metrics) {
    const period = String(m.date).slice(0, length);
    const entry = periods.get(period) ?? { period, cost: 0, budget: 0 };
    entry.cost += Number(m.cost) || 0;
    entry.budget += Number(m.budget) || 0;
    periods.set(period, entry);
  }
  return { data: [...periods.values()].sort((a, b) => a.period.localeCompare(b.period)) };
}

function widget(w: any) {
  return {
    id: w.id, page_id: w.pageId, name: w.name, widget_key: w.widgetKey, type: w.type, category: w.category,
    is_default: w.isDefault, default_position: w.defaultPosition, metadata: w.metadata,
    platform_configs: rows(w.platformConfigs).map((c: any) => ({
      id: c.id ?? w.id, widget_id: c.widgetId ?? w.id, platform: c.platform, component_name: c.componentName,
      default_cols: c.defaultCols, default_rows: c.defaultRows, min_cols: c.minCols,
      is_available: c.isAvailable, platform_metadata: c.platformMetadata,
    })),
  };
}

/** The snake_case field if the payload already has it (old shape), else its camelCase twin. */
const snakeOr = (o: any, snake: string, camel: string) => (snake in o ? o[snake] : o[camel]);

/** Saved-dashboard payloads went camelCase on prism-new; the dashboard services read snake_case. */
function dashboard(d: any) {
  return {
    ...d, user_id: snakeOr(d, 'user_id', 'userPid'), product_id: snakeOr(d, 'product_id', 'productPid'),
    page_id: snakeOr(d, 'page_id', 'pageId'), is_default: snakeOr(d, 'is_default', 'isDefault'),
  };
}

function dashboardWidget(w: any) {
  return {
    ...w, widget_id: snakeOr(w, 'widget_id', 'widgetId'), widget_key: snakeOr(w, 'widget_key', 'widgetKey'),
    is_visible: snakeOr(w, 'is_visible', 'isVisible'), col_span: snakeOr(w, 'col_span', 'colSpan'), row_span: snakeOr(w, 'row_span', 'rowSpan'),
    component_name: snakeOr(w, 'component_name', 'componentName'), custom_config: snakeOr(w, 'custom_config', 'customConfig'),
    source_page: snakeOr(w, 'source_page', 'sourcePage'), default_cols: snakeOr(w, 'default_cols', 'defaultCols'),
    default_rows: snakeOr(w, 'default_rows', 'defaultRows'), min_cols: snakeOr(w, 'min_cols', 'minCols'),
  };
}

/** Deck group: the old API's `group` / `when` / `index` names beside prism-new's; agents always an array. */
function deckGroup(g: any) {
  if (!g || typeof g !== 'object') return g;
  return {
    ...g,
    group: g.group ?? g.groupKey,
    when: g.when ?? g.whenToShow,
    index: g.index ?? g.groupIndex,
    isDeleted: g.isDeleted ?? false,
    agents: rows(g.agents),
  };
}

/** `{ group }` from get / create / update. */
const deckGroupBody: LegacyMap = (d) => ({ data: has(d, 'group') ? { ...d, group: deckGroup(d.group) } : d });

/** Deck slide: prism-new renames key / file / points / hash / html; deckName is the product in the path. */
function deckSlide(s: any, deckName?: string) {
  if (!s || typeof s !== 'object') return s;
  return {
    ...s,
    key: s.key ?? s.pageKey,
    file: s.file ?? s.fileName,
    points: s.points ?? rows(s.narrationPoints),
    hash: s.hash ?? s.contentHash,
    ...(s.html === undefined && s.htmlContent !== undefined ? { html: s.htmlContent } : {}),
    deckName: s.deckName ?? deckName,
    isDeleted: s.isDeleted ?? false,
  };
}

/** `{ slides: [...] }` (list / upload) or `{ slide }` (get / patch) with each slide in the old shape. */
const deckSlides: LegacyMap = (d, ctx) => {
  const deck = ctx.segments[1];
  if (Array.isArray(d?.slides)) return { data: { ...d, slides: d.slides.map((s: any) => deckSlide(s, deck)) } };
  if (has(d, 'slide')) return { data: { ...d, slide: deckSlide(d.slide, deck) } };
  return { data: d };
};

/** GET config (`dashboard` may be null: page snapshot only) and every `{ message, dashboard, widgets }` mutation. */
function dashboards(d: any): LegacyPayload {
  if (has(d, 'dashboard') && Array.isArray(d.widgets)) {
    return { data: { ...d, dashboard: d.dashboard && dashboard(d.dashboard), widgets: d.widgets.map(dashboardWidget) } };
  }
  if (Array.isArray(d?.dashboards)) return { data: d.dashboards.map(dashboard) };
  return { data: d };
}

const numericId = (id: unknown) => (typeof id === 'string' && /^\d+$/.test(id) ? Number(id) : id);

/**
 * App route in the old shape: prism-new nests sub-menus without `parentId` / `productId`, sends `moduleName`
 * for the old `module` object and the role's access as `hasAccess` for the old `roleAccess[]`.
 */
function appRoute(r: any, productId?: string, parentId?: string): any {
  if (!r || typeof r !== 'object') return r;
  return {
    ...r,
    parentId: r.parentId ?? parentId ?? null,
    productId: r.productId ?? productId,
    module: r.module ?? (r.moduleName ? { name: r.moduleName } : null),
    roleAccess: r.roleAccess ?? [{ hasAccess: !!r.hasAccess }],
    subMenus: rows(r.subMenus).map((s: any) => appRoute(s, productId, r.id)),
  };
}

/** `{ groups: [{ product, routes }] }` → the old array of groups, each route in the old shape. */
const appRouteGroups: LegacyMap = (d) => ({
  data: Array.isArray(d?.groups) ? d.groups.map((g: any) => ({ ...g, routes: rows(g.routes).map((r: any) => appRoute(r, g.product?.id)) })) : d,
});

function catalogPage(p: any) {
  return { id: p.id, product_id: p.productPid, name: p.name, slug: p.slug, sort_order: p.sortOrder, description: p.description, widgets: rows(p.widgets).map(widget) };
}

/** call-history/history → the per-type `history` list call-details renders. */
function callHistory(d: any, ctx: LegacyContext): LegacyPayload {
  if (!has(d, 'meet') && !has(d, 'assistant')) return { data: d };
  const type = d.type ?? ctx.params['type'];
  const history = type === 'AI_ASSISTANT'
    ? rows(d.assistant).flatMap((c: any) => rows(c.turns).map((t: any) => {
        const at = t.at ? Date.parse(t.at) / 1000 : undefined;
        return { conversationId: c.conversationId, user_text: t.query, agent_reply: t.response, timestamp_query: at, timestamp_reply: at };
      }))
    : rows(d.meet).map((m: any) => ({
        ...m,
        transcriptText: rows(m.transcript),
        audioUrls: rows(m.recordings).map((r: any) => ({ url: r.url, signedUrl: r.url, duration: r.durationSeconds, recordingStartTime: r.at })),
      }));
  return { data: { history, hasMoreHistory: !!d.hasMore } };
}

export const PRISM_NEW_LEGACY_MAPS: Record<string, LegacyMap> = {
  // navigation, products, roles
  'GET /auth/get-setting': unwrap('groups'),
  'GET /app-routes/list-routes': appRouteGroups,
  'GET /app-routes/list-role-route-access': appRouteGroups,
  'GET /app-routes/list-app-route-module': unwrap('modules'),
  // prism-new wraps the route; the edit page reads the old flat object.
  'GET /app-routes/get-route/{id}': (d) => ({ data: has(d, 'route') && d.route && typeof d.route === 'object' ? appRoute(d.route) : d }),
  'GET /product/list-product': unwrap('products'),
  'GET /countries': unwrap('countries'),
  'GET /roles/list': (d, ctx) => {
    if (!Array.isArray(d?.roles)) return { data: d };
    return ctx.params['page'] == null ? { data: d.roles } : { data: { roles: d.roles, ...pageInfo(ctx, d.pagination ?? d) } };
  },
  'GET /roles/{id}/users': (d) => ({ data: Array.isArray(d?.users) ? { success: true, data: d.users } : d }),
  'GET /auth/access-request/list': (d, ctx) => ({ data: Array.isArray(d?.requests) ? { success: true, data: d.requests, ...pageInfo(ctx, d.pagination) } : d }),
  'GET /audit-logs': (d, ctx) => {
    if (!Array.isArray(d?.entries)) return { data: d };
    const p = pageInfo(ctx, d.pagination);
    return { data: { items: d.entries.map((e: any) => ({ ...e, actorIP: e.actorIp })), meta: { total: p.total, page: p.page, limit: p.limit, totalPages: p.totalPages } } };
  },
  'GET /key-manager/list': (d, ctx) => ({ data: Array.isArray(d?.keys) ? { keyManagers: d.keys.map((k: any) => ({ ...k, productId: k.productPid })), pagination: pageInfo(ctx, d.pagination) } : d }),

  // live-call agent admin + consultant ring
  'GET /live-call-agent/{product}/prompts': (d, ctx) => ({ data: { product: ctx.segments[1], ...d } }),
  'GET /live-call-agent/{product}/tools': (d, ctx) => ({ data: { product: ctx.segments[1], ...d } }),
  'GET /live-call-agent/requests/{requestId}': (d) => ({ data: d?.request ? { ...d.request, requestId: d.request.id } : d }),

  // dashboard catalog
  'GET /catalog/products': (d) => ({ data: Array.isArray(d?.products) ? d.products.map((p: any) => ({ id: p.pid, name: p.name, slug: p.keyname, pages: rows(p.pages).map(catalogPage) })) : d }),
  'GET /catalog/products/{productPid}/pages': (d) => ({ data: Array.isArray(d?.pages) ? d.pages.map((p: any) => ({ ...p, id: numericId(p.id) })) : d }),
  'GET /catalog/pages/{pageId}/widgets': (d) => ({ data: Array.isArray(d?.widgets) ? d.widgets.map(widget) : d }),
  'GET /dashboards': dashboards,
  'POST /dashboards': dashboards,
  'POST /dashboards/{dashboardId}/widgets': dashboards,
  'PUT /dashboards/{dashboardId}/widgets': dashboards,
  'POST /dashboards/{dashboardId}/widgets/batch': dashboards,
  'DELETE /dashboards/{dashboardId}/widgets/{widgetId}': dashboards,
  'PATCH /dashboards/{dashboardId}/widgets/{widgetId}/visibility': dashboards,
  'PATCH /dashboards/{dashboardId}/widgets/{placementId}/size': dashboards,
  'PATCH /dashboards/{dashboardId}/widgets/{placementId}/custom-config': dashboards,
  'POST /dashboards/{dashboardId}/reset': dashboards,

  // release notes
  'GET /releases': (d, ctx) => {
    if (!Array.isArray(d?.releases)) return { data: d };
    const p = pageInfo(ctx, null, d.total);
    return {
      data: d.releases.map((r: any) => ({ ...r, productIds: r.productPids, products: r.products ?? rows(r.productPids).map((pid: number) => ({ pid })) })),
      pagination: { currentPage: p.page, perPage: p.limit, totalItems: p.total, totalPages: p.totalPages, hasNext: p.hasNext, hasPrev: p.hasPrev },
    };
  },

  // deck admin
  'GET /deck-admin/{product}/groups': (d) => ({
    data: Array.isArray(d?.groups) ? { ...d, groups: d.groups.map(deckGroup) } : d,
  }),
  'GET /deck-admin/{product}/groups/{groupKey}': deckGroupBody,
  'POST /deck-admin/{product}/groups': deckGroupBody,
  'PATCH /deck-admin/{product}/groups/{groupKey}': deckGroupBody,
  'GET /deck-admin/{product}/groups/{groupKey}/slides': deckSlides,
  'POST /deck-admin/{product}/groups/{groupKey}/slides': deckSlides,
  'GET /deck-admin/{product}/groups/{groupKey}/slides/{pageKey}': deckSlides,
  'PATCH /deck-admin/{product}/groups/{groupKey}/slides/{pageKey}': deckSlides,

  // campaign metrics
  'GET /campaign-metrics': campaignPeriods,
  'GET /campaign-metrics/by-date': unwrap('buckets'),
  'GET /campaign-metrics/overall-performance': (d, ctx) => ({
    data: has(d, 'cost') && !has(d, 'currentPeriod')
      ? {
          currentPeriod: { startDate: d.period?.startDate ?? ctx.params['startDate'], endDate: d.period?.endDate ?? ctx.params['endDate'], stats: { cost: d.cost, budget: d.budget } },
          growth: { cost: d.growth?.cost ?? 0, budget: d.growth?.budget ?? 0 },
        }
      : d,
  }),

  // seo
  'GET /seo/top-records': (d) => ({
    data: has(d, 'ratings')
      ? {
          // platformId keys the platform tabs; latestReviews rows are tracked by id.
          topRatings: rows(d.ratings).map((r: any) => ({
            ...withPlatform(r), platformId: r.platformId ?? r.platformPid,
            latestReviews: rows(r.latestReviews).map((v: any) => ({ ...v, id: v.id ?? v.pid })),
          })),
          topCityUsers: rows(d.cityUsers).map((c: any) => ({ ...c, date: month(c.date) })),
          topCountryStats: rows(d.countryStats).map(withCountry),
          topKeywordRanking: rows(d.keywordRankings),
          topAiOverviewKeywords: rows(d.aiOverviewKeywords),
        }
      : d,
  }),
  'GET /seo/click-performance-details': unwrap('buckets'),
  'GET /seo/rating-platforms': unwrap('platforms'),
  'GET /seo/city-users': (d, ctx) => ({ data: Array.isArray(d?.cities) ? { cityUsers: d.cities.map((c: any) => ({ ...c, date: month(c.date) })), pagination: pageInfo(ctx, null, d.total) } : d }),
  'GET /seo/ratings': (d, ctx) => ({ data: Array.isArray(d?.ratings) ? { ratings: d.ratings.map(withPlatform), pagination: d.pagination ?? pageInfo(ctx, null, d.total) } : d }),
  'GET /seo/reviews': (d, ctx) => ({ data: Array.isArray(d?.reviews) ? { reviews: d.reviews.map(withPlatform), pagination: d.pagination ?? pageInfo(ctx, null, d.total) } : d }),
  'GET /seo/metrics': (d, ctx) => ({ data: Array.isArray(d?.metrics) ? { seoMetrics: d.metrics.map((m: any) => ({ ...m, date: month(m.date) })), pagination: pageInfo(ctx, null, d.total) } : d }),
  'GET /seo/keyword-rankings': (d, ctx) => ({ data: Array.isArray(d?.rankings) ? { rankings: d.rankings, pagination: d.pagination ?? pageInfo(ctx, null, d.total) } : d }),
  'GET /seo/country-stats': (d, ctx) => ({ data: Array.isArray(d?.countries) ? { data: d.countries.map(withCountry), pagination: pageInfo(ctx, null, d.total) } : d }),
  'GET /seo/ai-overview-keywords': (d, ctx) => ({ data: Array.isArray(d?.keywords) ? { data: d.keywords, pagination: pageInfo(ctx, null, d.total) } : d }),
  'GET /seo/overall-seo-performance/{id}': (d) => ({ data: d?.current ? { ...d.current, growth: d.growth } : d }),

  // social platforms
  'GET /social-platform/list-social-platform': unwrap('platforms'),
  'GET /social-platform/list-social-stats': (d, ctx) => ({
    data: Array.isArray(d?.stats)
      ? { socialPlatforStats: d.stats.map((s: any) => ({ ...s, platformName: s.platform?.name, productName: s.product?.name })), pagination: pageInfo(ctx, d.pagination) }
      : d,
  }),
  'GET /social-platform/overall-performance-details/{id}': (d) => ({
    data: Array.isArray(d?.performanceStats)
      ? Object.fromEntries(SOCIAL_METRICS.map((k) => [k, d.performanceStats.reduce((sum: number, s: any) => sum + (Number(s[k]) || 0), 0)]))
      : d,
  }),
  'GET /social-platform/platform-performance-details': (d) => ({
    data: has(d, 'acrossPlatformStats')
      ? { performanceStats: rows(d.performanceStats), acrossPlatfromStats: rows(d.acrossPlatformStats).map((a: any) => ({ period: a.period, stats: rows(a.platforms) })) }
      : d,
  }),
  'GET /social-platform/overall-analytics/{id}': unwrap('performanceStats'),

  // ai summaries
  'GET /summary/cron-configs': (d, ctx) => {
    if (!Array.isArray(d?.configs) || d.pagination) return { data: d };
    const p = pageInfo(ctx, null, d.total);
    return { data: { configs: d.configs, pagination: { currentPage: p.page, perPage: p.limit, totalItems: p.total, totalPages: p.totalPages, hasNext: p.hasNext, hasPrev: p.hasPrev } } };
  },
  'GET /summary/sessions': (d, ctx) => ({ data: Array.isArray(d?.sessions) ? { sessionList: d.sessions, pagination: pageInfo(ctx, null, d.total) } : d }),
  'GET /summary/history/{sessionId}': (d) => ({ data: d?.session ?? d }),

  // calls, leads, agents
  'GET /call-history/list': (d, ctx) => {
    if (!Array.isArray(d?.groups)) return { data: d };
    const p = pageInfo(ctx, null, d.total);
    return {
      data: {
        groupedConversations: d.groups.map((g: any) => ({
          ...g, customerDetails: g.customer, agentDetails: g.agent, productDetails: rows(g.products), teamDetails: rows(g.teams),
          createdAt: g.firstAt, updatedAt: g.lastAt, totalConversations: g.calls,
        })),
        pagination: { page: p.page, limit: p.limit, totalRecords: p.total, totalPages: p.totalPages, hasMoreRecords: p.hasNext },
      },
    };
  },
  'GET /call-history/details': callHistory,
  'GET /customer-records/all': (d, ctx) => ({
    data: Array.isArray(d?.leads) ? { customerRecords: d.leads.map((l: any) => ({ ...l, pid: l.customerPid, id: l.customerId })), pagination: pageInfo(ctx, null, d.total) } : d,
  }),
  'GET /agent-records/all': (d, ctx) => ({
    data: Array.isArray(d?.agents)
      ? {
          agentRecords: d.agents.map((a: any) => ({
            ...a, pid: a.agentPid, id: a.agentId,
            coreAgentProduct: rows(a.products).map((p: any) => ({ pid: p.pid, product: p })),
            coreAgentTeam: rows(a.teams).map((t: any) => ({ pid: t.pid, team: t })),
          })),
          pagination: pageInfo(ctx, null, d.total),
        }
      : d,
  }),
  ...CALL_AUDIT_LEGACY_MAPS,
  ...BOT_BUILDER_LEGACY_MAPS,
  ...LEAD_AGENTS_LEGACY_MAPS,
  'GET /vocal-trans-eng/ongoing-calls': unwrap('calls'),
  'GET /vocal-trans-eng/all-leads-details': (d, ctx) => {
    if (!Array.isArray(d?.leads)) return { data: d };
    const p = pageInfo(ctx, null, d.total);
    return { data: d.leads, pagination: { currentPage: p.page, perPage: p.limit, totalItems: p.total, totalPages: p.totalPages } };
  },

  // extension download page
  'GET /extensions': unwrap('extensions'),
};

/** A CRM request as prism-new will receive it; fixes edit `params` / `segments` / `body` in place. */
export interface PrismNewRequest {
  params: Record<string, any>;
  segments: string[];
  /** A plain-object JSON body; absent for FormData or no body. */
  body?: Record<string, any>;
}

/** Resolves the CRM's product cuid (or numeric string) to the numeric pid prism-new validates. */
export type ProductPidLookup = (productId: unknown) => number | undefined;

type RequestFix = (request: PrismNewRequest, productPid: ProductPidLookup) => void;

const YEAR_MONTH = /^\d{4}-\d{2}$/;
const lastDayOf = (ym: string) => new Date(Date.UTC(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)), 0)).toISOString().slice(0, 10);
/** `YYYY-MM` → first (or last) day of that month; full timestamps → their date; anything else as sent. */
const toDay = (value: unknown, end = false) => {
  if (typeof value !== 'string') return value;
  if (YEAR_MONTH.test(value)) return end ? lastDayOf(value) : `${value}-01`;
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : value;
};
const toMonth = (value: unknown) => (typeof value === 'string' && /^\d{4}-\d{2}/.test(value) ? value.slice(0, 7) : value);

function dayRange(request: PrismNewRequest): void {
  const { params } = request;
  if (params['startDate'] != null) params['startDate'] = toDay(params['startDate']);
  if (params['endDate'] != null) params['endDate'] = toDay(params['endDate'], true);
}

/** The last 12 months as `YYYY-MM` bounds, for calls the CRM sends without a range. */
function lastTwelveMonths(now = new Date()): { start: string; end: string } {
  const yearAgo = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 11, 1));
  return { start: yearAgo.toISOString().slice(0, 7), end: now.toISOString().slice(0, 7) };
}

/** A missing range becomes the last 12 months: the SEO page used to pin a 2024–2025 window that aged out. */
function defaultRange({ params }: PrismNewRequest): void {
  const range = lastTwelveMonths();
  params['startDate'] = params['startDate'] ?? range.start;
  params['endDate'] = params['endDate'] ?? range.end;
}

const seoDayRange: RequestFix = (request) => {
  defaultRange(request);
  dayRange(request);
};

const seoMonthRange: RequestFix = (request) => {
  defaultRange(request);
  request.params['startDate'] = toMonth(request.params['startDate']);
  request.params['endDate'] = toMonth(request.params['endDate']);
};

/** prism-new wants the numeric `productPid` where the CRM sends the product cuid as `productId`. */
function productPidParam(request: PrismNewRequest, productPid: ProductPidLookup): void {
  const { params } = request;
  if (params['productPid'] != null || params['productId'] == null) return;
  const pid = productPid(params['productId']);
  if (pid == null) return;
  params['productPid'] = pid;
  delete params['productId'];
}

/** Save bodies: prism-new requires the numeric `productPid` and has no cuid `productId` field. */
const productPidBody: RequestFix = ({ body }, productPid) => {
  if (!body || body['productPid'] != null || body['productId'] == null) return;
  const pid = productPid(body['productId']);
  if (pid == null) return;
  body['productPid'] = pid;
  delete body['productId'];
};

/** SEO list filters: prism-new's `date` is a day (`YYYY-MM-DD`); the CRM sends the month. */
const dayParam: RequestFix = ({ params }) => {
  if (params['date'] != null) params['date'] = toDay(params['date']);
};

/** Monthly SEO / campaign saves: prism-new's `date` is `YYYY-MM-DD`; the CRM sends the month. */
const monthlySave: RequestFix = ({ body }) => {
  if (body?.['date'] != null) body['date'] = toDay(body['date']);
};

const monthlySaveWithPid: RequestFix = (request, productPid) => {
  monthlySave(request, productPid);
  productPidBody(request, productPid);
};

/** Release saves: prism-new links products by numeric `productPids`; the form sends `productIds`. */
const releaseProducts: RequestFix = ({ body }, productPid) => {
  if (!body || body['productPids'] != null || !Array.isArray(body['productIds'])) return;
  body['productPids'] = body['productIds'].map((id: unknown) => productPid(id)).filter((pid: number | undefined) => pid != null);
  delete body['productIds'];
};

/** A product cuid or numeric string → the integer pid prism-new validates; anything unresolvable as sent. */
const asPid = (value: unknown, productPid: ProductPidLookup) => (value == null || typeof value === 'number' ? value : productPid(value) ?? value);

/** Dashboard widget refs: prism-new takes `widget_id` / `source_page_id` as digit strings. */
const widgetRef = (w: any) => {
  if (!w || typeof w !== 'object') return w;
  const ref = { ...w };
  if (typeof ref['widget_id'] === 'number') ref['widget_id'] = String(ref['widget_id']);
  if (typeof ref['source_page_id'] === 'number') ref['source_page_id'] = String(ref['source_page_id']);
  return ref;
};

/** Drops `null` fields, for bodies whose optional swagger fields are not nullable. */
const withoutNulls = (value: any) =>
  value && typeof value === 'object' ? Object.fromEntries(Object.entries(value).filter(([, v]) => v !== null)) : value;

/** Route form: componentName / componentPath are null for a route without a component; the swagger's are not nullable. */
const routeComponent: RequestFix = ({ body }) => {
  for (const key of ['componentName', 'componentPath']) if (body?.[key] === null) delete body[key];
};

/** A query key the CRM sends under the old API's name → prism-new's name for it. */
const renameParam = (from: string, to: string): RequestFix => ({ params }) => {
  if (params[from] == null || params[to] != null) return;
  params[to] = params[from];
  delete params[from];
};

const productPidSegment = (index: number): RequestFix => (request, productPid) => {
  const pid = productPid(decodeURIComponent(request.segments[index] ?? ''));
  if (pid != null) request.segments[index] = String(pid);
};

const seoTable: RequestFix = (request, productPid) => {
  productPidParam(request, productPid);
  if (request.params['date'] != null) request.params['date'] = toDay(request.params['date']);
};

/** Request-side differences from the swagger: prism-new rejects (400) what the CRM sends today. */
export const PRISM_NEW_REQUEST_FIXES: Record<string, RequestFix> = {
  ...RAG_REQUEST_FIXES,
  'GET /seo/top-records': seoDayRange,
  'GET /seo/click-performance-details': seoMonthRange,
  'GET /seo/keyword-rankings': seoTable,
  'GET /seo/country-stats': seoTable,
  'GET /seo/ai-overview-keywords': seoTable,
  'GET /seo/keywords/{productId}': productPidSegment(2),
  'GET /seo/ai-keywords/{productId}': productPidSegment(2),
  'GET /seo/overall-seo-performance/{id}': (request, productPid) => {
    productPidSegment(2)(request, productPid);
    seoDayRange(request, productPid);
  },
  // startDate/endDate are required (YYYY-MM); the CRM omits them until a range is picked, so default
  // to the last 12 months.
  'GET /social-platform/platform-performance-details': ({ params }) => {
    const range = lastTwelveMonths();
    params['startDate'] = toMonth(params['startDate']) || range.start;
    params['endDate'] = toMonth(params['endDate']) || range.end;
  },
  'GET /seo/metrics': dayParam,
  'GET /seo/city-users': dayParam,
  'GET /seo/ratings': dayParam,
  'GET /seo/reviews': dayParam,
  'POST /seo/metrics': monthlySave,
  'POST /seo/ratings': monthlySave,
  'POST /seo/city-users': monthlySave,
  'POST /seo/keyword-ranking': monthlySaveWithPid,
  'POST /seo/ai-overview-keywords': monthlySaveWithPid,
  'POST /seo/country-stats': monthlySaveWithPid,
  'POST /seo/create': monthlySave,
  'POST /campaign-metrics': (request, productPid) => {
    monthlySave(request, productPid);
    if (request.body?.['productPid'] != null) request.body['productPid'] = asPid(request.body['productPid'], productPid);
  },
  // Page dashboards (pin / customize) pass the selected product's cuid; prism-new's product_id is the pid.
  'GET /dashboards': ({ params }, productPid) => {
    if (params['product_id'] != null) params['product_id'] = asPid(params['product_id'], productPid);
  },
  // Dashboard saves: widget / page ids are digit strings, product_id an integer.
  'POST /dashboards': ({ body }, productPid) => {
    if (!body) return;
    if (typeof body['page_id'] === 'number') body['page_id'] = String(body['page_id']);
    if (body['product_id'] != null) body['product_id'] = asPid(body['product_id'], productPid);
    else if (body['product_id'] === null) delete body['product_id']; // optional, not nullable (no product resolved)
    if (Array.isArray(body['widget_ids'])) body['widget_ids'] = body['widget_ids'].map(widgetRef);
  },
  'POST /dashboards/{dashboardId}/widgets': (request) => {
    if (request.body) request.body = widgetRef(request.body);
  },
  'POST /dashboards/{dashboardId}/widgets/batch': ({ body }) => {
    if (Array.isArray(body?.['widgets'])) body['widgets'] = body['widgets'].map(widgetRef);
  },
  // botId / metadata / productName are optional but not nullable.
  'POST /call-audit/events': ({ body }) => {
    if (Array.isArray(body?.['events'])) body['events'] = body['events'].map(withoutNulls);
  },
  // user_id is a positive integer; the embed host may pass any string (left out when not numeric).
  'POST /bot-builder/chat': ({ body }) => {
    if (body?.['user_id'] == null) return;
    const id = String(body['user_id']);
    if (/^\d+$/.test(id) && Number(id) > 0) body['user_id'] = Number(id);
    else delete body['user_id'];
  },
  // The sidebar's last-resort fallback sends the product cuid as productPid.
  'GET /summary/sessions': ({ params }, productPid) => {
    if (params['productPid'] != null) params['productPid'] = asPid(params['productPid'], productPid);
  },
  'GET /customer-records/all': renameParam('search', 'searchString'),
  // The release page's product filter; prism-new filters by productPid only.
  'GET /releases': productPidParam,
  'POST /releases': releaseProducts,
  'PATCH /releases/{id}': releaseProducts,
  // status ACTIVE / INACTIVE is prism-new's isActive true / false.
  'GET /roles/list': ({ params }) => {
    if (params['status'] == null) return;
    const status = String(params['status']).toUpperCase();
    delete params['status'];
    if (params['isActive'] == null && (status === 'ACTIVE' || status === 'INACTIVE')) params['isActive'] = String(status === 'ACTIVE');
  },
  // Old lowercase lead statuses → prism-new's enum, where sales_pitch_* is SALE_PITCH_*.
  'GET /vocal-trans-eng/all-leads-details': ({ params }) => {
    if (typeof params['status'] === 'string') params['status'] = params['status'].toUpperCase().replace(/^SALES_PITCH/, 'SALE_PITCH');
  },
  // The CRM only rejects through this endpoint and sends just the note; prism-new requires the decision.
  'PATCH /auth/access-request/{id}/status': ({ body }) => {
    if (body && body['status'] == null) body['status'] = 'REJECTED';
  },
  // Audit log panel filters: startDate / endDate days → from / to date-times; no ordering params in the swagger.
  'GET /audit-logs': ({ params }) => {
    const [start, end] = [toDay(params['startDate']), toDay(params['endDate'])];
    for (const key of ['startDate', 'endDate', 'orderBy', 'orderDirection']) delete params[key];
    if (typeof start === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(start)) params['from'] = `${start}T00:00:00.000Z`;
    if (typeof end === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(end)) params['to'] = `${end}T23:59:59.999Z`;
  },
  // Audit writes: oldValue / newValue / note are optional but not nullable; `source` is not a swagger field.
  'POST /audit-logs': (request) => {
    if (!request.body) return;
    const { source, ...body } = withoutNulls(request.body);
    request.body = body;
  },
  // User form: blank inputs arrive as null; the swagger's fields are optional but not nullable.
  'POST /user': (request) => {
    if (request.body) request.body = withoutNulls(request.body);
  },
  // An emptied phone number is cleared with '' (null is not allowed); other blanks are left out.
  'PUT /user/{id}': (request) => {
    if (!request.body) return;
    const phoneNumber = request.body['phoneNumber'] === null ? '' : request.body['phoneNumber'];
    request.body = withoutNulls({ ...request.body, phoneNumber });
  },
  // Profile: the swagger only turns 2FA on here (isTwoFAEnabled enum [true]); false is left out.
  'PATCH /user/profile': ({ body }) => {
    if (body && body['isTwoFAEnabled'] !== true) delete body['isTwoFAEnabled'];
  },
  'POST /app-routes/create-route': routeComponent,
  'PUT /app-routes/update-route': routeComponent,
};

/** Old endpoints that prism-new serves under a new name (swagger): CRM path → prism-new path. */
export const PRISM_NEW_RENAMED_ENDPOINTS: Record<string, (params: Record<string, any>) => string | null> = {
  // Old `list` grouped calls per customer-agent pair; prism-new calls that `grouped`.
  'GET /call-history/list': () => 'call-history/grouped',
  // Old `details` took the agent/customer pair; prism-new's `details` takes one conversationPid.
  'GET /call-history/details': (params) => (params['conversationPid'] == null ? 'call-history/history' : null),
};
